import { isDefined } from "@clipboard-health/util-ts";
import {
  useInfiniteGetQuery,
  type UseInfiniteGetQueryOptions,
} from "@src/appV2/api/useInfiniteGetQuery";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type AxiosError } from "axios";

import {
  type GetPlacementWorkplacesForWorkerQuery,
  getPlacementWorkplacesForWorkerQuerySchema,
  type GetPlacementWorkplacesResponse,
  getWorkplacePlacementsResponseSchema,
} from "../types/placementWorkplaces.schema";

function getPlacementWorkplacesUrl(workerId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/placement-workplaces`;
}

interface GetPaginatedPlacementWorkplacesParams {
  workerId: string;
  filter?: GetPlacementWorkplacesForWorkerQuery["filter"];
  pageSize?: number;
}

export function useGetPaginatedPlacementWorkplaces(
  params: GetPaginatedPlacementWorkplacesParams,
  options: UseInfiniteGetQueryOptions<GetPlacementWorkplacesResponse, AxiosError> = {}
) {
  const { workerId, filter, pageSize = 20 } = params;

  return useInfiniteGetQuery<
    GetPlacementWorkplacesForWorkerQuery,
    GetPlacementWorkplacesResponse,
    GetPlacementWorkplacesResponse,
    string
  >({
    url: getPlacementWorkplacesUrl(workerId),
    queryParams: {
      filter,
      page: {
        size: pageSize,
      },
      groupBy: "workplace",
    },
    responseSchema: getWorkplacePlacementsResponseSchema,
    requestSchema: getPlacementWorkplacesForWorkerQuerySchema,
    getNextPageParam: (lastPage) => getCursorFromNextLink(lastPage.links.next),
    nextPageContext: async ({ pageParam, queryParams }) => {
      return {
        queryParams: {
          ...queryParams,
          page: {
            ...queryParams.page,
            cursor: pageParam,
          },
        },
      };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_PLACEMENT_WORKPLACES_FAILURE,
      userErrorMessage: "Something went wrong while loading the placement workplaces.",
    },
    ...options,
  });
}

function getCursorFromNextLink(nextLink?: string): string | undefined {
  if (!isDefined(nextLink)) {
    return undefined;
  }

  try {
    const url = new URL(nextLink, environmentConfig.REACT_APP_BASE_API_URL);

    return url.searchParams.get("page[cursor]") ?? undefined;
  } catch {
    return undefined;
  }
}
